import { useCallback, useEffect } from 'react'
import { MEETING_VIEW_ID } from '../../components/meeting/meeting-view-id'
import { useSetViewTab, useUpdateTopViewContext, useViewTab } from '../../view-atoms'
import { useViewContext } from '../../view-context'
import {
  hasMeetingTabs,
  isMeetingTab,
  resolveMeetingTab,
  stampMeetingTab,
  stampOpenerAsk,
  type MeetingTab,
} from './meeting-tabs'
import type { MeetingViewContext, MeetingViewModel } from './use-meeting-view-model'

export interface MeetingTabState {
  tab: MeetingTab
  setTab: (tab: MeetingTab) => void
}

/**
 * The Meeting View's active tab, persisted per history entry so back/forward
 * lands on the tab the user left. Picks are stamped with the variant they were
 * made under and expire when the meeting crosses into the other one, which is
 * what lands a finished meeting on its recap. See `resolveMeetingTab`.
 *
 * With `hasChatTab`, the meeting's thread is a tab rather than a drawer, so
 * the entry's `chat` flag is what selects it.
 */
export function useMeetingTab(
  model: Pick<MeetingViewModel, 'variant' | 'audioNoteId'>,
  hasBriefTab: boolean,
  { hasChatTab }: { hasChatTab: boolean },
): MeetingTabState {
  const { entryKey, context } = useViewContext<MeetingViewContext>()
  const stored = useViewTab(entryKey)
  const setViewTab = useSetViewTab()
  const updateViewContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  const { variant, audioNoteId } = model
  // The entry's context outlives the build that wrote it — `viewHistoryAtom`
  // persists it through a hard refresh — so the declared type is a claim about
  // this build's callers, not about what's actually stored. An ask naming a tab
  // this build dropped has to read as no ask at all: passed through, it would
  // bind `Tabs` to a panel that no longer mounts and paint an empty body.
  const initialTab = isMeetingTab(context?.initialTab) ? context.initialTab : undefined

  // Record the opener's ask as this entry's pick, so it expires with the
  // recording like any other, then clear it so Back and a hard refresh don't
  // re-ask. Cleared from the context rather than gated on a mount-local ref:
  // the entry outlives its component.
  useEffect(() => {
    if (initialTab === undefined) return
    setViewTab(entryKey, stampOpenerAsk(initialTab, variant, !!audioNoteId))
    updateViewContext((prev) => (prev?.initialTab ? { ...prev, initialTab: undefined } : prev))
  }, [initialTab, variant, audioNoteId, entryKey, setViewTab, updateViewContext])

  // `context.chat` selects the Chat tab, the same flag that raises the drawer
  // on a meeting with no tabs yet. So a submit or "Resume chat" lands on the
  // tab, and a thread open when recording stops stays on it. A stored Chat pick
  // never selects it and falls back to Transcript.
  const isChatOpen = hasChatTab && hasMeetingTabs(model) && Boolean(context?.chat)
  const resolved = resolveMeetingTab({ stored, variant, initialTab, hasBriefTab })
  const tab = isChatOpen ? 'chat' : resolved === 'chat' ? 'transcript' : resolved
  const setTab = useCallback(
    (next: MeetingTab) => {
      if (hasChatTab) {
        const chat = next === 'chat'
        updateViewContext((prev) => (prev && Boolean(prev.chat) !== chat ? { ...prev, chat } : prev))
      }
      if (next !== 'chat') setViewTab(entryKey, stampMeetingTab(next, variant))
    },
    [entryKey, hasChatTab, setViewTab, updateViewContext, variant],
  )

  return { tab, setTab }
}
